// Edit Profil. Everything that is a setting about the traveller, in one
// place: photo, name, bio, language, default privacy. Round 7 merged the
// old Preferences block in here rather than leaving it as a second panel,
// because "language" is a fact about you, not a separate department.
//
// THE RULE THIS SCREEN EXISTS TO PROTECT: `avatarSeed` is identity, not a
// derivative of the name. Saving sends displayName and bio only, never a
// seed, so a traveller who fixes a typo keeps the same face. Regenerating
// the character is its own control, pressed on purpose.
//
// Language and privacy are one label plus one compact control per row. No
// three button rows shouting options, no section header repeated over each
// one, and no boxed panel nested inside a boxed panel.
import { useEffect, useRef, useState } from "react";
import { Icon, IconGallery, IconRefresh } from "@traveldiary/ui";
import { TravellerAvatar } from "../components/TravellerAvatar";
import { goBack } from "../lib/router";
import { PRIVACY_VALUES, type Privacy } from "../lib/types";
import { uuid } from "../lib/uuid";
import { LOCALES, type Locale } from "../lib/i18n";
import { useApp } from "../state/app";

export function EditProfileScreen() {
  const {
    t,
    locale,
    setLocale,
    me,
    authUser,
    displayName,
    setDisplayName,
    saveProfile,
    uploadAvatarPhoto,
    defaultPrivacy,
    setDefaultPrivacy,
  } = useApp();

  const user = me ?? authUser;
  const [name, setName] = useState(user?.displayName ?? displayName);
  const [bio, setBio] = useState(me?.bio ?? "");
  const [state, setState] = useState<"idle" | "saving" | "saved" | "failed">("idle");
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!user) return;
    setName(user.displayName);
  }, [user?.id, user?.displayName]);

  useEffect(() => {
    setBio(me?.bio ?? "");
  }, [me?.id, me?.bio]);

  async function save(): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed) return;
    setState("saving");
    try {
      // displayName and bio ONLY. No avatarSeed: renaming is not a redraw.
      await saveProfile({ displayName: trimmed.slice(0, 60), bio: bio.slice(0, 300) });
      setState("saved");
    } catch {
      // The API may not carry /users/me yet. The name still has to stick on
      // this device, because it is what the local diary shows.
      setDisplayName(trimmed);
      setState("failed");
    }
  }

  async function regenerate(): Promise<void> {
    setState("saving");
    try {
      // "regenerate" is the API's own sentinel for minting a fresh random
      // face. Sending uuid() instead was a real bug: the server accepts only
      // "regenerate" or 16 lowercase hex characters, so a uuid came back 400
      // and this button silently did nothing.
      await saveProfile({ avatarSeed: "regenerate" });
      setState("saved");
    } catch {
      setState("failed");
    }
  }

  async function pickPhoto(file: File | undefined): Promise<void> {
    if (!file) return;
    setState("saving");
    try {
      await uploadAvatarPhoto(file, file.name);
      setState("saved");
    } catch {
      setState("failed");
    }
  }

  return (
    <div className="screen-pad">
      <section className="edit-identity">
        <TravellerAvatar user={user} fallbackName={displayName} size="lg" isSelf />
        <div className="row-actions">
          <button
            type="button"
            className="btn btn-quiet"
            onClick={() => fileRef.current?.click()}
            disabled={state === "saving"}
          >
            <Icon size={18}>
              <IconGallery />
            </Icon>
            {t("profile.photo")}
          </button>
          <button
            type="button"
            className="btn btn-quiet"
            onClick={() => void regenerate()}
            disabled={state === "saving"}
          >
            <Icon size={18}>
              <IconRefresh />
            </Icon>
            {t("profile.regenerate")}
          </button>
        </div>
        <input
          ref={fileRef}
          className="sr-only"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(event) => void pickPhoto(event.target.files?.[0])}
        />
        <p className="field-note">{t("profile.avatarNote")}</p>
      </section>

      <label className="field">
        <span className="field-label">{t("settings.displayName")}</span>
        <input
          className="input"
          value={name}
          maxLength={60}
          placeholder={t("settings.displayNamePlaceholder")}
          onChange={(event) => {
            setName(event.target.value);
            setState("idle");
          }}
        />
      </label>

      <label className="field">
        <span className="field-label">{t("profile.bio")}</span>
        <textarea
          className="input input-area"
          rows={3}
          value={bio}
          maxLength={300}
          placeholder={t("profile.bioPlaceholder")}
          onChange={(event) => {
            setBio(event.target.value);
            setState("idle");
          }}
        />
      </label>

      <div className="setting-list">
        <label className="setting-row">
          <span className="setting-label">{t("settings.language")}</span>
          <select
            className="select"
            value={locale}
            onChange={(event) => setLocale(event.target.value as Locale)}
          >
            {LOCALES.map((value) => (
              <option key={value} value={value}>
                {value === "id" ? t("settings.indonesian") : t("settings.english")}
              </option>
            ))}
          </select>
        </label>

        <label className="setting-row">
          <span className="setting-label">{t("settings.privacyDefault")}</span>
          <select
            className="select"
            value={defaultPrivacy}
            onChange={(event) => setDefaultPrivacy(event.target.value as Privacy)}
          >
            {PRIVACY_VALUES.map((value) => (
              <option key={value} value={value}>
                {t(`privacy.${value}`)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="field-note">{t("settings.privacyDefaultHint")}</p>

      <div className="row-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void save()}
          disabled={state === "saving" || !name.trim()}
        >
          {state === "saving" ? t("capture.saving") : t("profile.save")}
        </button>
        <button type="button" className="btn btn-quiet" onClick={() => goBack("profil")}>
          {t("common.cancel")}
        </button>
      </div>
      {state === "saved" ? (
        <p className="field-note" role="status">
          {t("profile.saved")}
        </p>
      ) : null}
      {state === "failed" ? (
        <p className="field-note" role="status">
          {t("profile.failed")}
        </p>
      ) : null}
    </div>
  );
}
